// @flow
import {ROOT_PATH} from '../toURL';
import {LANDING_PAGE_URL_PREFIXES} from '../models/SearchUrl';

const localeRegex = RegExp(/\/[a-zA-Z]{2}-[a-zA-Z]{2}\//);

export const normalizeString = (value: ?string): string => {
    if (!value) {
        return '';
    }

    return decodeURIComponent(value).replace(/\+/g, ' ');
};

export const encodeURIComponentString = (value: ?string): string => {
    if (!value) {
        return '';
    }

    return encodeURIComponent(value);
};

export const intParser = (value: ?string): number => {
    value = normalizeString(value);
    let result: number = parseInt(value, 10);

    return isNaN(result) ? -1 : result;
};

export const floatParser = (value: ?string): number => {
    value = normalizeString(value);
    let result: number = parseFloat(value);

    return isNaN(result) ? -1 : result;
};

export const stringParser = (value: ?string): string => {
    return normalizeString(value);
};

export const booleanParser = (value: ?string): boolean => {
    if (!value) {
        return false;
    }

    return value === 'true' || value === '1';
};

/**
 * Returns the root path of a given url
 * http://www.homeaway.com/results/fo/bar/land -> /results
 * @private
 * @param {url} value url
 */
export const stripToRoot = (value: ?string): string => {
    if (!value) {
        return ROOT_PATH;
    }

    // remove protocol
    let strippedValue = value.replace(/http(s)?:\/\//i, '');

    // strip off query string
    strippedValue = strippedValue.split('?', 1).join('');

    // strip off hash
    strippedValue = strippedValue.split('#', 1).join('');

    // strip off domain
    if (!strippedValue.startsWith('/')) {
        const tokens = strippedValue.split('/');
        strippedValue = `/${tokens.slice(1).join('/')}`;
    }

    strippedValue = strippedValue.replace(localeRegex, '/');

    // grab root path
    return strippedValue.split('/', 2).join('/');
};

/**
 * Checks whether root path is valid
 * @private
 * @param root
 * @returns {boolean}
 */
export const isValidRoot = (root: string): boolean => {
    return [...LANDING_PAGE_URL_PREFIXES, '/search', '/results']
        .join('|')
        .includes(root);
};

/**
 * Converts a date string to YYYY-mm-dd format
 * @private
 * Be careful when changing this function. This way of parsing dates should be revisited to be TZ independent.
 * @param {value} date as a string
 */
export const formatDateToISO = (value: string): string | null => {
    try {
        return new Date(decodeURIComponent(value)).toISOString().split('T')[0];
    } catch (e) {
        // swallow
        return null;
    }
};

export const parseNullOrString = (rawValue: string | null) => {
    const value = stringParser(rawValue);
    if (value === 'undefined' || value === 'null') {
        return null;
    }
    if (value.match(/^\-?\d+\.\d+$/)) {
        return floatParser(value);
    }
    if (value.match(/^\-?\d+$/)) {
        return intParser(value);
    }
    if (value.toLowerCase() === 'true') {
        return true;
    }
    if (value.toLowerCase() === 'false') {
        return false;
    }
    return value;
};

/**
 * Parses a string value and attempts the type cast it to an int, float, or boolean, falling back to
 * a string.
 * @private
 */
export const multiPrimitiveParser = (
    rawValue: string | Array<string> | null
): string | number | boolean | Array<*> | null => {
    if (Array.isArray(rawValue)) {
        return rawValue.map((v) => parseNullOrString(v));
    }
    return parseNullOrString(rawValue);
};

export const toTitleCase = (str: string) =>
    str.replace(
        /\w\S*/g,
        (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );

export const canonicalizeKeywords = (str: string = ''): string =>
    str
        .toLowerCase()
        .replace(/[%,?.@#$^&*_\\/~!<>\[\]='"|;]/g, '')
        .replace(/\s\s*/g, '-');
